import React from 'react';
import Breadcrumb from '../components/Breadcrumb';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { BookOpen, Mail, Target, Users, Heart } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumb items={[{ label: 'About', path: '/about' }]} />

        {/* Header */}
        <div className="mb-12">
          <BookOpen className="w-16 h-16 text-emerald-500 mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">About Options Analogies</h1>
          <p className="text-2xl text-emerald-500 font-medium mb-4">
            See options differently.
          </p>
          <p className="text-lg text-gray-300">
            Built by Datatomic Labs to make options education clear, accessible, and memorable.
          </p>
        </div>

        {/* Mission */}
        <section className="mb-12">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <Target className="w-10 h-10 text-emerald-500 mb-4" />
              <h2 className="text-2xl font-bold text-white mb-4">Our Mission</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Options education is often buried in jargon, complex Greeks, and intimidating terminology. We believe there's a better way.
              </p>
              <p className="text-gray-300 leading-relaxed">
                By mapping options mechanics to familiar contracts—insurance policies, real estate agreements, warranties—we help retail investors build accurate mental models. The result? Faster comprehension, better retention, and more confident decision-making.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Who We Serve */}
        <section className="mb-12">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <Users className="w-10 h-10 text-emerald-500 mb-4" />
              <h2 className="text-2xl font-bold text-white mb-4">Who We Serve</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-emerald-400 mb-2">Retail Investors</h3>
                  <p className="text-gray-300">
                    Self-directed traders looking to understand cash-secured puts, covered calls, and the Wheel strategy without wading through dense textbooks.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-emerald-400 mb-2">Educators & Mentors</h3>
                  <p className="text-gray-300">
                    Learners, financial coaches, and study group leaders who need clear frameworks and ready-made materials for self-study and group learning.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-emerald-400 mb-2">Curious Learners</h3>
                  <p className="text-gray-300">
                    Anyone who's heard about "selling options for income" and wants to understand the real mechanics, obligations, and risks involved.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Our Approach */}
        <section className="mb-12">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <Heart className="w-10 h-10 text-emerald-500 mb-4" />
              <h2 className="text-2xl font-bold text-white mb-4">Our Approach</h2>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Plain English, Always</p>
                    <p className="text-gray-400 text-sm">Smart high-schooler reading level. No unnecessary jargon.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Evidence-Minded</p>
                    <p className="text-gray-400 text-sm">We focus on mechanics, math, and risk—not hype or promises.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Honest About Risks</p>
                    <p className="text-gray-400 text-sm">Every strategy includes risk callouts and failure scenarios.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">Analogy-Driven</p>
                    <p className="text-gray-400 text-sm">Leverage mental models you already have to understand new concepts faster.</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Contact */}
        <section className="mb-12">
          <div className="glass rounded-xl p-8 text-center">
            <Mail className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">Get in Touch</h2>
            <p className="text-gray-300 mb-6">
              Questions? Feedback? Want to collaborate on educational content?
            </p>
            <Button className="bg-emerald-600 hover:bg-emerald-700" asChild>
              <a href="mailto:contact@datatomiclabs.com">contact@datatomiclabs.com</a>
            </Button>
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Ready to learn?</h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="outline" asChild>
                <Link to="/csp">Start with CSP</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/analogies">Browse Analogies</Link>
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" asChild>
                <Link to="/teaching-kits">Get Learner's Kits</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;